import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  DollarSign,
  Clock,
  Star,
  Package,
  LogOut,
  ChevronDown,
  ChevronUp,
  Activity,
  Camera,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { MarketerDashboard, Order } from "../types";
import apiService from "../services/api";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { state, updateMarketerAvailability, logout } = useApp();
  const [dashboardData, setDashboardData] = useState<MarketerDashboard | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    loadDashboardData();

    // Listen for new orders from polling
    const handleNewOrders = () => {
      loadDashboardData();
    };

    const handleOrderUpdates = () => {
      loadDashboardData();
    };

    window.addEventListener("newOrders", handleNewOrders);
    window.addEventListener("orderUpdates", handleOrderUpdates);

    return () => {
      window.removeEventListener("newOrders", handleNewOrders);
      window.removeEventListener("orderUpdates", handleOrderUpdates);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getMarketerDashboard();
      console.log("[Dashboard] Loaded dashboard data:", data);
      console.log(
        "[Dashboard] Recent orders count:",
        data.recentOrders?.length
      );
      console.log("[Dashboard] Recent orders:", data.recentOrders);
      setDashboardData(data);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvailabilityToggle = async () => {
    if (!state.marketer) return;

    try {
      setAvailabilityLoading(true);
      await updateMarketerAvailability(!state.marketer.is_available);
    } catch (error) {
      console.error("Failed to update availability:", error);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      submitted_for_buying: "text-blue-600 bg-blue-100",
      accepted: "text-orange-600 bg-orange-100",
      buying: "text-purple-600 bg-purple-100",
      waiting_delivery: "text-yellow-600 bg-yellow-100",
      submitted_for_delivery: "text-blue-600 bg-blue-100",
      delivered: "text-green-600 bg-green-100",
      cancelled: "text-red-600 bg-red-100",
    };
    return colors[status as keyof typeof colors] || "text-gray-600 bg-gray-100";
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <div className="min-w-0">
                <img
                  className="w-[150px] sm:w-[200px]"
                  src="/assets/emaketa-straight-logo.webp"
                  alt="emaketa"
                />
                <p className="text-sm text-gray-500">
                  {state.marketer?.phone_number}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Availability Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Available:</span>
                <button
                  onClick={handleAvailabilityToggle}
                  disabled={availabilityLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    state.marketer?.is_available ? "bg-primary" : "bg-gray-200"
                  } ${availabilityLoading ? "opacity-50" : ""}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      state.marketer?.is_available
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Logout Button */}
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Summary Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-gray-600">Active:</span>
                <span className="font-semibold text-blue-600">
                  {dashboardData?.activeOrders || 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <span className="text-sm text-gray-600">Today:</span>
                <span className="font-semibold text-orange-600">
                  {dashboardData?.ordersToday || 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-600">Earnings:</span>
                <span className="font-semibold text-green-600">
                  ${dashboardData?.earningsToday?.toFixed(2) || "0.00"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/menu-snapshots")}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
              >
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Snap Prices</span>
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
              >
                <Activity className="w-4 h-4" />
                <span>{showStats ? "Hide" : "Show"} Statistics</span>
                {showStats ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Collapsible Statistics Section */}
        {showStats && (
          <div className="mb-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Orders */}
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Buys
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData?.totalOrders || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Active Orders */}
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Active Buys
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData?.activeOrders || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Total Earnings */}
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Earnings
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${dashboardData?.totalEarnings?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Average Rating
                    </p>
                    <div className="flex items-center gap-1">
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardData?.averageRating?.toFixed(1) || "0.0"}
                      </p>
                      <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Today's Performance
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Orders Today</span>
                    <span className="font-semibold">
                      {dashboardData?.ordersToday || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Earnings Today</span>
                    <span className="font-semibold">
                      ${dashboardData?.earningsToday?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Completion Rate</span>
                    <span className="font-semibold">
                      {dashboardData?.completionRate?.toFixed(1) || "0.0"}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Stats
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Completed Orders</span>
                    <span className="font-semibold text-green-600">
                      {dashboardData?.completedOrders || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Cancelled Orders</span>
                    <span className="font-semibold text-red-600">
                      {dashboardData?.cancelledOrders || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        state.marketer?.is_available
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {state.marketer?.is_available
                        ? "Available"
                        : "Unavailable"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders List - Primary Focus */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Your Orders</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage and track all your assigned orders
              </p>
            </div>
            <button
              onClick={loadDashboardData}
              className="px-4 py-2 text-sm text-primary border border-primary hover:bg-primary hover:text-white rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>

          {dashboardData?.recentOrders?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Orders Yet
              </h3>
              <p className="text-gray-500">
                {state.marketer?.is_available
                  ? "You're available! New orders will appear here."
                  : "Set yourself as available to receive orders."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboardData?.recentOrders?.map((order: Order) => {
                const currentStatus = order.currentStatus?.status || "";

                return (
                  <div
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    className="border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all overflow-hidden cursor-pointer bg-white"
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-6 h-6 text-primary" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3 mb-2 flex-wrap">
                            <p className="font-semibold text-gray-900 text-lg">
                              #{order.orderId}
                            </p>
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(
                                currentStatus
                              )}`}
                            >
                              {formatStatus(currentStatus)}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 text-sm text-gray-500 mb-2 flex-wrap">
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              {order.market?.name || "Any Market"}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>
                              {order.marketList.marketListItem?.length || 0}{" "}
                              items
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>
                              {new Date(order.date).toLocaleDateString()}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                            <div className="min-w-0">
                              <p className="text-2xl font-bold text-primary truncate">
                                {order.cost.toFixed(0)} FCFA
                              </p>
                              <p className="text-xs text-gray-500">
                                Shopping budget
                              </p>
                            </div>
                            <button className="px-6 py-2 bg-primary hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm flex-shrink-0">
                              View Details →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {dashboardData?.recentOrders &&
            dashboardData.recentOrders.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <button
                  onClick={() => navigate("/orders")}
                  className="text-primary hover:text-orange-600 text-sm font-medium inline-flex items-center gap-2"
                >
                  View All Orders
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
